import { getSupaClient, getWorkspace } from "../../lib/supabase";
import TagsMsgs from "apps/dots-frontend/types/tags_msgs";
import { useQuery, useQueryClient } from "react-query";
import { useState } from "react";
import { TAG_COLORS } from "../../utils/constants";

/**
 * fetch all stored Tags for discord messages
 */
export function useFetchDiscordTagMessages() {
  const supabase = getSupaClient();
  const guildId = localStorage.getItem("guild-id") || "";

  const fetchTags = async () => {
    const { data, error } = await supabase
      .from<TagsMsgs>("tags_msgs")
      .select("*")
      .match({ guild: guildId })
      .order("id", { ascending: false });

    if (data && data.length >= 0 && Array.isArray(data)) {
      return data;
    } else if (error) {
      throw new Error(error.message);
    } else {
      throw new Error(`Failed to fetch tags for guild with the id: ${guildId}`);
    }
  };

  return useQuery<TagsMsgs[], Error>(["guildSavedMsgTags", guildId], fetchTags);
}

/**
 * fetch all stored Tags for discord messages
 */
export default function useDiscordTagMessages(
  setMainAlert: (type: "error" | "warning", message: string) => void
): {
  loadingTags: boolean;
  createTag: (
    newTagName: string,
    currentNumberOfTags: TagsMsgs[]
  ) => Promise<{ success: boolean }>;
  deleteTag: (tagId: number) => void;
  relateTag: (
    tagId: number,
    messageId: string
  ) => Promise<{ success: boolean }>;
  removeTag: (
    tagId: number,
    messageId: string
  ) => Promise<{ success: boolean }>;
} {
  const supabase = getSupaClient();
  const workspace = getWorkspace(supabase);
  const guildId = localStorage.getItem("guild-id") || "";
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const createTag = async (newTagName: string, currentTags: TagsMsgs[]) => {
    // Check if we already have a tag with the same name, if so show an alert message
    if (
      currentTags
        .map((item) => item.name.toLowerCase())
        .includes(newTagName.toLowerCase())
    ) {
      setMainAlert("warning", "This tag name already exists");
      return { success: false };
    }
    const tagColor = TAG_COLORS[currentTags.length % TAG_COLORS.length];
    const { error } = await supabase
      .from("tags_msgs")
      .insert({ name: newTagName, guild: guildId, color: tagColor, workspace });
    if (error) {
      console.error(error.message);
      setMainAlert("error", "Tag could not be saved");
      return { success: false };
    } else {
      window.analytics.track("Saved messages - tag created");
      return { success: true };
    }
  };

  const deleteTag = async (tagId: number) => {
    setLoading(true);
    const { error } = await supabase.from("tags_msgs").delete().eq("id", tagId);
    if (error) {
      console.error(error.message);
      setMainAlert("error", "Tag could not be deleted");
    }
    window.analytics.track("Saved messages - tag deleted");
    queryClient.invalidateQueries("guildfetchAllSavedMessages");
    setLoading(false);
  };

  const relateTag = async (tagId: number, messageId: string) => {
    const { error } = await supabase.from("discord_saved_msg_tags").insert({
      tag: tagId,
      saved_msg_id: messageId,
      guild: guildId,
      workspace,
    });

    if (error) {
      console.error(error.message);
      setMainAlert(
        "error",
        "Tag could not be added to this message, try again or contact us."
      );
      return { success: false };
    } else {
      window.analytics.track("Saved messages - tag selected");
      queryClient.invalidateQueries("guildfetchAllSavedMessages");
      return { success: true };
    }
  };

  const removeTag = async (tagId: number, messageId: string) => {
    const { error } = await supabase
      .from("discord_saved_msg_tags")
      .delete()
      .match({ tag: tagId, saved_msg_id: messageId, guild: guildId });
    if (error) {
      console.error(error.message);
      setMainAlert(
        "error",
        "Tag could not be removed from this message, try again or contact us."
      );
      return { success: false };
    } else {
      window.analytics.track("Saved messages - tag deselected");
      queryClient.invalidateQueries("guildfetchAllSavedMessages");
      return { success: true };
    }
  };

  return {
    loadingTags: loading,
    createTag,
    deleteTag,
    relateTag,
    removeTag,
  };
}
